/** @file solveCcd: cyclic coordinate descent IK for chains of three or more bones. */
import { quatRotateVec3 } from '@zilo/pose3d-math/quat/quatRotateVec3';
import type { Vec3 } from '@zilo/pose3d-math/vec3/vec3';
import { vec3Add } from '@zilo/pose3d-math/vec3/vec3Add';
import { vec3Length } from '@zilo/pose3d-math/vec3/vec3Length';
import { vec3Sub } from '@zilo/pose3d-math/vec3/vec3Sub';
import { ikFromTo } from '@zilo/pose3d-pose/_internal/ikFromTo';
import { ikNormalize } from '@zilo/pose3d-pose/_internal/ikNormalize';
import type { IkInput, IkResult } from '@zilo/pose3d-pose/ik/ikTypes';

/**
 * From the joint nearest the end back to the pivot, turns the sub-chain about that joint (`ikFromTo`) so the end points
 * at the target; repeats until the end is within `tol` or `maxIter` sweeps. `reached` = the target is within the
 * chain's total length of the pivot.
 */
export function solveCcd(input: IkInput, out: IkResult, maxIter = 16, tol = 1e-4): IkResult {
  const p = input.points.map((v): Vec3 => [v[0], v[1], v[2]]);
  const t = input.target;
  const last = p.length - 1;
  const end = () => p[last] ?? t;
  for (let iter = 0; iter < maxIter && vec3Length(vec3Sub(end(), t)) >= tol; iter++) {
    for (let j = last - 1; j >= 0; j--) {
      const joint = p[j] ?? t;
      const q = ikFromTo(ikNormalize(vec3Sub(end(), joint)), ikNormalize(vec3Sub(t, joint)));
      for (let k = j + 1; k <= last; k++) p[k] = vec3Add(joint, quatRotateVec3(q, vec3Sub(p[k] ?? joint, joint)));
    }
  }
  let length = 0;
  for (let k = 0; k < last; k++) length += vec3Length(vec3Sub(input.points[k + 1] ?? t, input.points[k] ?? t));
  out.points = p;
  out.reached = vec3Length(vec3Sub(t, input.points[0] ?? t)) <= length;
  return out;
}
